import { apiFetch } from './http';

// ─────────────────────────────────────────────────────────────────────
// «SEND BETALINGSLENKE» — the browser half of
// `POST /organization/bookings/:id/payment-link` (#1137).
//
// Staff name the member a priced staff booking is for; the server makes that
// member its owner, holds it for payment and mails them a link. The request
// carries the booking's `book_…` id and the member's roster id (the player's
// legacy id, as `GET /members/roster` hands it out) — nothing about money: the
// server prices the booking itself. The answer is a CODE; the screen decides
// the sentence.
// ─────────────────────────────────────────────────────────────────────

/** How the member was told, as the server reports it. */
export type PaymentLinkMail =
  'sent' | 'not_configured' | 'no_address' | 'refused' | 'provider_error';

export type SendPaymentLinkResult =
  /** Linked. The booking is payable in the member's app whatever `mail` says. */
  | {
      readonly state: 'linked';
      readonly playerName: string;
      readonly amountMinor: number;
      readonly mail: PaymentLinkMail;
    }
  /**
   * Refused, with the server's code (`not_on_roster`, `unpriceable`, …) and,
   * where it names one, the narrower `reason` (`unpriceable` / `no_booked_price`,
   * #1361) — the screen says the reason when it has a sentence for it.
   */
  | { readonly state: 'refused'; readonly code: string; readonly reason?: string }
  /** The request never got an answer we understand. Nothing is known. */
  | { readonly state: 'error'; readonly status: number };

const MAILS: readonly PaymentLinkMail[] = [
  'sent',
  'not_configured',
  'no_address',
  'refused',
  'provider_error',
];

export async function sendPaymentLink(
  bookingId: string,
  playerId: string,
): Promise<SendPaymentLinkResult> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(bookingId)}/payment-link`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ playerId }),
    });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.linked === true) {
    const mail = MAILS.includes(body.mail as PaymentLinkMail)
      ? (body.mail as PaymentLinkMail)
      : 'provider_error';
    return {
      state: 'linked',
      playerName: typeof body.playerName === 'string' ? body.playerName : '',
      amountMinor: typeof body.amountMinor === 'number' ? body.amountMinor : 0,
      mail,
    };
  }
  if (typeof body?.error === 'string') {
    return typeof body.reason === 'string'
      ? { state: 'refused', code: body.error, reason: body.reason }
      : { state: 'refused', code: body.error };
  }
  return { state: 'error', status: res.status };
}

// ─────────────────────────────────────────────────────────────────────
// «TREKK TILBAKE BETALINGSLENKE» — `DELETE …/payment-link` (#1310).
//
// The booking goes back to the club: the server retires any open payment at
// the provider first, clears the member as owner and keeps the hold. As for
// sending, the answer is a CODE and the screen decides the sentence.
// ─────────────────────────────────────────────────────────────────────

export type WithdrawPaymentLinkResult =
  /** Withdrawn. `attemptCancelled`: an open payment was cancelled on the way. */
  | { readonly state: 'withdrawn'; readonly attemptCancelled: boolean }
  /** Refused, with the server's code (`not_linked`, `payment_unconfirmed`, …). */
  | { readonly state: 'refused'; readonly code: string }
  | { readonly state: 'error'; readonly status: number };

export async function withdrawPaymentLink(bookingId: string): Promise<WithdrawPaymentLinkResult> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(bookingId)}/payment-link`, {
      method: 'DELETE',
    });
  } catch {
    return { state: 'error', status: 0 };
  }
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  if (res.status === 200 && body?.withdrawn === true) {
    return { state: 'withdrawn', attemptCancelled: body.attemptCancelled === true };
  }
  if (typeof body?.error === 'string') return { state: 'refused', code: body.error };
  return { state: 'error', status: res.status };
}

// ─────────────────────────────────────────────────────────────────────
// WHAT THE PANEL SHOULD SAY — `GET …/payment-link` (#1364, #1369).
//
// A staff edit of what the mail quoted — the price (#1364), the court, the
// date or the time (#1369) — voids the link the member was mailed (owner
// decisions 2026-11-04). The panel asks whether that is what happened to this
// booking, so it can say why and offer a new link — to the same member,
// preselected. Display only: a failed read shows the panel as it was before.
// ─────────────────────────────────────────────────────────────────────

/** Why the server says the link was voided — the codes it writes, and no others. */
export type PaymentLinkVoidReason = 'price_changed' | 'slot_changed' | 'price_and_slot_changed';
const VOID_REASONS: readonly PaymentLinkVoidReason[] = [
  'price_changed',
  'slot_changed',
  'price_and_slot_changed',
];

export type PaymentLinkState =
  /** Linked now, or never voided: nothing to say. */
  | { readonly state: 'plain' }
  /**
   * The last link was voided by an edit, and `reason` says which kind: the
   * price, the court or time, or both. `playerId` is the roster id, or null.
   */
  | {
      readonly state: 'voided';
      readonly reason: PaymentLinkVoidReason;
      readonly playerId: string | null;
    }
  /** The read failed or was refused; the panel says nothing extra. */
  | { readonly state: 'unknown' };

export async function fetchPaymentLinkState(bookingId: string): Promise<PaymentLinkState> {
  let res: Response;
  try {
    res = await apiFetch(`/organization/bookings/${encodeURIComponent(bookingId)}/payment-link`);
  } catch {
    return { state: 'unknown' };
  }
  if (res.status !== 200) return { state: 'unknown' };
  const body = (await res.json().catch(() => null)) as Record<string, unknown> | null;
  const voided = body?.voided as Record<string, unknown> | null | undefined;
  const reason = VOID_REASONS.find((r) => r === voided?.reason);
  if (body?.linked === false && voided && reason !== undefined) {
    return {
      state: 'voided',
      reason,
      playerId: typeof voided.playerId === 'string' ? voided.playerId : null,
    };
  }
  return { state: 'plain' };
}
